/**
 * useTenantBroadcast Hook — Cross-Tab Tenant Synchronization
 *
 * Synchronizes tenant selection across multiple browser tabs using BroadcastChannel API.
 * Falls back to localStorage polling on browsers without BroadcastChannel support.
 *
 * Features:
 * - Real-time cross-tab sync via BroadcastChannel
 * - Automatic fallback to localStorage polling (500ms interval)
 * - Graceful degradation on old browsers
 * - No parameters required (uses TenantContext directly)
 *
 * Usage:
 * ```tsx
 * // In root app component (or anywhere after TenantProvider)
 * export function App() {
 *   useTenantBroadcast();  // Just call it, handles everything
 *   return <...>
 * }
 * ```
 */

import { useEffect, useCallback } from 'react';
import { useTenantContext } from '@/context/TenantContext';

/**
 * Message format for cross-tab communication
 */
interface TenantChangedMessage {
  type: 'TENANT_CHANGED';
  payload: {
    selectedTenantId: number | null;
    isInGlobalView: boolean;
  };
}

/**
 * Hook for cross-tab tenant synchronization
 *
 * Automatically syncs tenant selection across all open tabs in the same domain.
 * Primary method: BroadcastChannel API
 * Fallback method: localStorage polling (500ms interval)
 *
 * No parameters required. Call once in app root (preferably in main App component).
 *
 * Example:
 * ```tsx
 * function App() {
 *   useTenantBroadcast();  // Syncs tenant across tabs
 *   return <Router />;
 * }
 * ```
 */
export const useTenantBroadcast = (): void => {
  const { selectedTenantId, isInGlobalView, setSelectedTenantId, setGlobalView } =
    useTenantContext();

  // Silent debug helper to satisfy lint (no-console)
  const debug = (..._args: unknown[]) => { void _args; };

  /**
   * Handle incoming broadcast messages from other tabs
   */
  const handleBroadcastMessage = useCallback(
    (event: MessageEvent<TenantChangedMessage>) => {
      const { type, payload } = event.data;

      if (type === 'TENANT_CHANGED') {
        if (payload.isInGlobalView) {
          setGlobalView(true);
        } else if (payload.selectedTenantId !== null) {
          setSelectedTenantId(payload.selectedTenantId);
        }
      }
    },
    [setSelectedTenantId, setGlobalView]
  );

  useEffect(() => {
    let channel: BroadcastChannel | null = null;
    let pollInterval: NodeJS.Timeout | null = null;
    let lastStoredValue: string | null = null;

    // Permite injetar construtor customizado em testes
    const BroadcastChannelCtor =
      (globalThis as any).__TEST_BROADCAST_CHANNEL__ ??
      (typeof BroadcastChannel !== 'undefined' ? BroadcastChannel : null);

    try {
      // Try primary method: BroadcastChannel API (native ou injetado)
      if (!BroadcastChannelCtor) {
        throw new Error('BroadcastChannel unavailable');
      }

      channel = new BroadcastChannelCtor('tenant-switcher-channel');
      if (channel) {
        channel.addEventListener('message', handleBroadcastMessage);
        debug('[TenantBroadcast] Using BroadcastChannel API');
      }
    } catch (error) {
      debug('[TenantBroadcast] BroadcastChannel unavailable');
    }

    // Sempre habilita polling de localStorage para robustez e compatibilidade com testes
    lastStoredValue = localStorage.getItem('selectedTenantId');
    pollInterval = setInterval(() => {
      try {
        const currentValue = localStorage.getItem('selectedTenantId');

        // If value changed in another tab or via test, update context
        if (currentValue !== lastStoredValue) {
          lastStoredValue = currentValue;

          if (currentValue === null) {
            setGlobalView(true);
          } else {
            const tenantId = parseInt(currentValue, 10);
            if (!isNaN(tenantId)) {
              setSelectedTenantId(tenantId);
            }
          }
        }
      } catch (pollError) {
        debug('[TenantBroadcast] localStorage polling error:', pollError);
      }
    }, 500);

    /**
     * Broadcast outgoing change to other tabs when context changes
     */
    const broadcastChange = () => {
      const message: TenantChangedMessage = {
        type: 'TENANT_CHANGED',
        payload: {
          selectedTenantId,
          isInGlobalView,
        },
      };

      try {
        if (channel) {
          // Send via BroadcastChannel if available
          channel.postMessage(message);
        } else if (!pollInterval) {
          // If neither channel nor polling active, try one-time broadcast
          // (This shouldn't happen in normal operation)
          debug('[TenantBroadcast] No broadcast method available');
        }
      } catch (broadcastError) {
        debug('[TenantBroadcast] Failed to broadcast change:', broadcastError);
      }
    };

    // Broadcast whenever tenant changes
    broadcastChange();

    /**
     * Cleanup: Close channel and clear polling interval
     */
    return () => {
      if (channel) {
        channel.removeEventListener('message', handleBroadcastMessage);
        channel.close();
      }
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [selectedTenantId, isInGlobalView, handleBroadcastMessage, setSelectedTenantId, setGlobalView]);
};

export default useTenantBroadcast;
